import React, { useState, useEffect, useContext } from 'react';
import { X, Save, Plus, CheckCircle, XCircle, Camera, Eye, FileText } from 'lucide-react';
import axios from 'axios';
import { UserContext } from '../../contexts/UserContext';
// Removed jsPDF imports - now using backend PDF generation

const InspectionModal = ({
  isOpen,
  onClose,
  equipmentId,
  equipmentName,
  equipmentSerialNumber,
  equipmentData = null,
  onSave,
  multiInspectionMode = false,
  currentInspectionIndex = 0,
  totalInspections = 0,
  onSkip,
  inspectionTableData = [],
  isTableMode = false,
  inspectionTableId = null
}) => {
  const { user } = useContext(UserContext);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    inspection_date: '',
    inspector: '',
    validity_date: '',
    status: 'ok',
    comments: '',
    image: null,
    inspection_type: 'internal', // 'internal' or 'external'
    external_company_name: '',
    inspection_file: null // PDF file for external inspections
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [rightPanelTab, setRightPanelTab] = useState('details'); // 'details' or 'history'
  const [uploadingPdfForInspection, setUploadingPdfForInspection] = useState(null);
  const [selectedPdfFiles, setSelectedPdfFiles] = useState({});
  
  // Auto-detect table mode if inspectionTableData is provided
  const detectedTableMode = isTableMode || (inspectionTableData && inspectionTableData.length > 0);
  const detectedTableId = inspectionTableId || (inspectionTableData && inspectionTableData.length > 0 ? inspectionTableData[0].id : null);

  // Get today's date and time in YYYY-MM-DDTHH:MM format
  const getTodayDateTime = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Get today's date in YYYY-MM-DD format (for validity date)
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };


  // Get date one year from today in YYYY-MM-DD format
  const getOneYearFromToday = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  };

  // Get logged in user's full name
  const getLoggedInUserName = () => {
    // Use UserContext first (most up-to-date data)
    if (user) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      if (fullName) {
        console.log('DEBUG: Using UserContext for inspector name:', fullName);
        return fullName;
      }
      if (user.username) {
        console.log('DEBUG: Using UserContext username for inspector:', user.username);
        return user.username;
      }
    }
    
    // Fallback to session storage
    const fullUserData = sessionStorage.getItem('fullUserData');
    if (fullUserData) {
      const userData = JSON.parse(fullUserData);
      return `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || '';
    }
    
    // Fallback to localStorage
    const userData = localStorage.getItem('userData');
    if (userData) {
      const userData_parsed = JSON.parse(userData);
      return userData_parsed.username || '';
    }
    
    return '';
  };

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy'}/api`;

  // Generate PDF for inspection report using backend PPE report generator
  const generateInspectionPDF = async (inspectionData, equipmentData, loggedUserCompanyData) => {
    try {
      console.log('🔄 Generating PDF for inspection using backend...');
      console.log('📊 Equipment ID:', equipmentData.id);
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // Important for PDF download
      };

      // Use the same endpoint as EquipmentLogBook for consistent styling  
      const fullUrl = `${API_BASE_URL}/ppe-report/`;
      console.log('📤 Making request to:', fullUrl);
      console.log('📤 API_BASE_URL:', API_BASE_URL);
      console.log('📤 Request data:', { equipment_ids: [equipmentData.id], report_type: 'single' });
      
      const response = await axios.post(`${API_BASE_URL}/ppe-report/`, {
        equipment_ids: [equipmentData.id],
        report_type: 'single'
      }, config);

      console.log('✅ PDF generated successfully using backend');
      console.log('📄 Response type:', typeof response.data);
      console.log('📄 Response size:', response.data?.size || 'unknown');
      console.log('📄 Response content type:', response.headers?.['content-type']);
      
      return response.data; // Return the blob directly
      
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });
      throw new Error(`Failed to generate PDF report: ${error.response?.status || error.message}`);
    }
  };

  // Delete inspection table entry after successful inspection
  const deleteInspectionTableEntry = async () => {
    if (!detectedTableMode || !detectedTableId) {
      console.log('📋 Not in table mode or no table ID provided, skipping deletion');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error('❌ No authentication token found for inspection table deletion');
        return;
      }

      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      console.log('🗑️ Deleting inspection table entry with ID:', detectedTableId);
      
      await axios.delete(`${API_BASE_URL}/inspectiontable/${detectedTableId}/`, config);
      
      console.log('✅ Successfully deleted inspection table entry');
    } catch (error) {
      console.error('❌ Error deleting inspection table entry:', error);
      // Don't show error to user as this is a background operation
      // The inspection was successful, this is just cleanup
    }
  };

  // Fetch full user data if not in session storage
  const fetchFullUserData = async () => {
    const fullUserData = sessionStorage.getItem('fullUserData');
    if (!fullUserData) {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        try {
          const token = localStorage.getItem('access_token');
          const config = {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          };
          const response = await axios.get(`${API_BASE_URL}/users/${user.id}/`, config);
          sessionStorage.setItem('fullUserData', JSON.stringify(response.data));
        } catch (error) {
          console.error('Error fetching full user data:', error);
        }
      }
    }
  };

  useEffect(() => {
    if (isOpen && equipmentId) {
      fetchInspections();
      fetchFullUserData();
      
      // Check if there's inspection table data with image for this equipment
      let inspectionTableImage = null;
      
      if (inspectionTableData.length > 0 && inspectionTableData[0].table) {
        const tableItem = inspectionTableData[0].table.find(item => 
          parseInt(item.id) === equipmentId
        );
        
        if (tableItem && tableItem.image) {
          inspectionTableImage = tableItem.image;
          console.log('📋 Found image for equipment', equipmentId, ':', inspectionTableImage);
        }
      }
      
      // Automatically open the New Inspection form
      setShowForm(true);
      // Set default values for new inspection
      // If user is not an inspector, default to external inspection type
      const defaultInspectionType = user?.inspector ? 'internal' : 'external';
      setFormData({
        inspection_date: defaultInspectionType === 'internal' ? getTodayDateTime() : '',
        inspector: defaultInspectionType === 'internal' ? getLoggedInUserName() : '',
        validity_date: defaultInspectionType === 'internal' ? getOneYearFromToday() : '',
        status: 'ok',
        comments: '',
        image: null,
        inspection_type: defaultInspectionType,
        external_company_name: '',
        inspection_file: null
      });
      
      // If there's inspection table data with image, set it as preview and convert to File
      if (inspectionTableImage) {
        setImagePreview(inspectionTableImage);
        
        // Convert image URL to File object for form submission
        fetch(inspectionTableImage)
          .then(response => response.blob())
          .then(blob => {
            const fileName = inspectionTableImage.split('/').pop() || 'inspection_image.jpg';
            const file = new File([blob], fileName, { type: blob.type });
            setFormData(prev => ({
              ...prev,
              image: file
            }));
            console.log('📋 Image converted to File object:', fileName);
          })
          .catch(error => {
            console.error('Error converting inspection table image:', error);
            setImagePreview(null);
          });
      } else {
        setImagePreview(null);
      }
    } else if (!isOpen) {
      // Reset form when modal is closed
      setShowForm(false);
      setFormData({
        inspection_date: '',
        inspector: '',
        validity_date: '',
        status: 'ok',
        comments: '',
        image: null,
        inspection_type: 'internal',
        external_company_name: '',
        inspection_file: null
      });
      setImagePreview(null);
    }
  }, [isOpen, equipmentId]); // Removed inspectionTableData from dependencies to prevent infinite loop

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      console.log('Fetching inspections from:', `${API_BASE_URL}/inspection/?safety_equipment=${equipmentId}`);
      console.log('With config:', config);

      const response = await axios.get(`${API_BASE_URL}/inspection/?safety_equipment=${equipmentId}`, config);
      
      console.log('Inspection API response:', response);
      console.log('Response data:', response.data);
      console.log('Response data length:', response.data?.length);
      
      const inspectionData = Array.isArray(response.data) ? response.data : 
                            response.data.results ? response.data.results : [];
      
      console.log('Processed inspection data:', inspectionData);
      
      // Sort inspections by inspection_date descending, then created_date descending (newest first)
      const sortedInspections = inspectionData.sort((a, b) => {
        const dateA = new Date(a.inspection_date);
        const dateB = new Date(b.inspection_date);
        // If inspection dates are the same, sort by created_date descending
        if (dateA.getTime() === dateB.getTime()) {
          const createdA = new Date(a.created_date);
          const createdB = new Date(b.created_date);
          return createdB - createdA; // Descending order (newest first)
        }
        return dateB - dateA; // Descending order (newest first)
      });
      
      setInspections(sortedInspections);
    } catch (error) {
      console.error('Error fetching inspections:', error);
      if (error.response?.status === 403) {
        setError('Access denied. You may not have permission to view inspections or your session has expired.');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(error.response?.data?.detail || error.message || 'Failed to load inspections');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'image') {
      const file = files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          setError('Please select a valid image file');
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setError('Image file size must be less than 5MB');
          return;
        }

        setFormData(prev => ({
          ...prev,
          image: file
        }));

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFormData(prev => ({
          ...prev,
          image: null
        }));
        setImagePreview(null);
      }
    } else if (name === 'inspection_file') {
      const file = files[0];
      if (file) {
        // Validate file type
        if (file.type !== 'application/pdf') {
          setError('Please select a valid PDF file');
          return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError('PDF file size must be less than 10MB');
          return;
        }

        setFormData(prev => ({
          ...prev,
          inspection_file: file
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          inspection_file: null
        }));
      }
    } else if (name === 'status') {
      if (value === 'non-ok') {
        // When status is set to 'non-ok', automatically set validity date to today
        setFormData(prev => ({
          ...prev,
          [name]: value,
          validity_date: getTodayDate()
        }));
      } else {
        // When status is 'ok', reset validity date to one year from today
        setFormData(prev => ({
          ...prev,
          [name]: value,
          validity_date: getOneYearFromToday()
        }));
      }
    } else if (name === 'inspection_type') {
      // When switching inspection type, adjust fields accordingly
      if (value === 'internal') {
        // Internal: set current date/time and auto-fill inspector
        setFormData(prev => ({
          ...prev,
          [name]: value,
          inspection_date: getTodayDateTime(),
          inspector: getLoggedInUserName(),
          validity_date: getOneYearFromToday(),
          external_company_name: '',
          inspection_file: null
        }));
      } else if (value === 'external') {
        // External: clear inspection date, validity date, and inspector for manual entry
        setFormData(prev => ({
          ...prev,
          [name]: value,
          inspection_date: '',
          inspector: '',
          validity_date: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      console.log('🔍 Debug info:', {
        token: token ? `${token.substring(0, 20)}...` : 'No token',
        equipmentId,
        formData,
        API_BASE_URL
      });
      
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      };

      // First, get equipment data for PDF generation
      console.log('📥 Fetching equipment data for PDF generation...');
      const equipmentResponse = await axios.get(`${API_BASE_URL}/safetyequipment/${equipmentId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const equipmentData = equipmentResponse.data;
      console.log('✅ Equipment data fetched:', equipmentData);

      // Get logged user's company data from UserContext
      console.log('📥 Getting logged user company data for PDF header...');
      console.log('🔍 UserContext data:', user);
      
      // Use UserContext data directly - company info is available directly in user object
      const loggedUserCompanyData = user || {
        company_name: 'Unknown Company',
        company: null
      };
      
      console.log('✅ Using user company data:', {
        companyName: loggedUserCompanyData.company_name,
        companyId: loggedUserCompanyData.company,
        hasCompanyName: !!loggedUserCompanyData.company_name,
        hasCompanyId: !!loggedUserCompanyData.company
      });

      // Create FormData for file upload
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('inspection_date', formData.inspection_date);
      formDataToSubmit.append('validity_date', formData.validity_date);
      formDataToSubmit.append('status', formData.status);
      formDataToSubmit.append('comments', formData.comments);
      formDataToSubmit.append('safety_equipment', equipmentId);
      formDataToSubmit.append('inspection_type', formData.inspection_type);

      // Fields specific to inspection type
      if (formData.inspection_type === 'internal') {
        // For internal inspections, inspector is auto-filled by backend from logged-in user
        // But we send it anyway for compatibility
        formDataToSubmit.append('inspector', formData.inspector);
      } else if (formData.inspection_type === 'external') {
        // For external inspections, we need inspector name and company name
        formDataToSubmit.append('inspector', formData.inspector);
        formDataToSubmit.append('external_company_name', formData.external_company_name);

        // External inspections require PDF file upload
        if (formData.inspection_file) {
          formDataToSubmit.append('inspection_file', formData.inspection_file);
        }
      }

      if (formData.image) {
        formDataToSubmit.append('inspection_photo', formData.image);
      }

      console.log('📤 Sending inspection data with image:', {
        ...formData,
        image: formData.image ? formData.image.name : null,
        safety_equipment: equipmentId
      });

      // Create the inspection
      const inspectionResponse = await axios.post(`${API_BASE_URL}/inspection/`, formDataToSubmit, config);
      const savedInspection = inspectionResponse.data;
      console.log('✅ Inspection saved successfully:', savedInspection);

      // Generate PDF report ONLY for internal inspections
      // For external inspections, PDF was already uploaded by the user
      if (formData.inspection_type === 'internal') {
        try {
          console.log('📄 Generating PDF for internal inspection...');
          const pdfBlob = await generateInspectionPDF(
            {
              ...savedInspection,
              inspection_photo: savedInspection.inspection_photo
            },
            equipmentData,
            loggedUserCompanyData
          );

          // Upload PDF to the inspection
          const pdfFormData = new FormData();
          const pdfFilename = `inspection_report_${savedInspection.id}_${new Date().getTime()}.pdf`;
          pdfFormData.append('inspection_file', pdfBlob, pdfFilename);

          console.log('📤 Uploading PDF to inspection...');
          console.log('📤 PDF blob size:', pdfBlob?.size || 'unknown');
          console.log('📤 PDF filename:', pdfFilename);
          console.log('📤 Inspection ID:', savedInspection.id);

          // Create separate config for file upload
          const uploadConfig = {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
          };

          const uploadResponse = await axios.patch(
            `${API_BASE_URL}/inspection/${savedInspection.id}/`,
            pdfFormData,
            uploadConfig
          );

          console.log('✅ PDF uploaded successfully to inspection');
          console.log('📄 Upload response:', uploadResponse.status, uploadResponse.statusText);

        } catch (pdfError) {
          console.error('❌ Error generating/uploading PDF:', pdfError);
          // Don't fail the entire inspection if PDF generation fails
          setError(`Inspection saved, but PDF generation failed: ${pdfError.message}`);
        }
      } else {
        console.log('📄 External inspection - PDF already uploaded by user');
      }
      
      // If in table mode, delete the inspection table entry since inspection is complete
      if (detectedTableMode) {
        await deleteInspectionTableEntry();
      }

      await fetchInspections();
      resetForm();
      onSave && onSave();
    } catch (error) {
      console.error('❌ Error saving inspection:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      console.error('Response headers:', error.response?.headers);
      
      if (error.response?.status === 403) {
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Access denied';
        setError(`403 Forbidden: ${errorMsg}. Check server logs for more details.`);
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(error.response?.data?.detail || error.message || 'Failed to save inspection');
      }
    } finally {
      setLoading(false);
    }
  };


  const resetForm = () => {
    setFormData({
      inspection_date: getTodayDateTime(),
      inspector: getLoggedInUserName(),
      validity_date: getOneYearFromToday(),
      status: 'ok',
      comments: '',
      image: null
    });
    setImagePreview(null);
    // Keep form open after saving (especially useful for multi-inspection mode)
    // setShowForm(false); - removed so form stays open
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInspectionStatus = (inspection) => {
    if (!inspection.validity_date) return { color: 'gray', text: 'No validity date' };

    const validityDate = new Date(inspection.validity_date);
    const today = new Date();
    const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));

    if (inspection.status === 'non-ok') {
      return { color: 'red', text: 'Failed Inspection' };
    } else if (daysUntilExpiry < 0) {
      return { color: 'red', text: `Expired ${Math.abs(daysUntilExpiry)} days ago` };
    } else if (daysUntilExpiry <= 30) {
      return { color: 'orange', text: `Expires in ${daysUntilExpiry} days` };
    } else if (daysUntilExpiry <= 60) {
      return { color: 'yellow', text: `Expires in ${daysUntilExpiry} days` };
    } else {
      return { color: 'green', text: `Valid for ${daysUntilExpiry} days` };
    }
  };

  const handlePdfFileSelect = (inspectionId, file) => {
    if (!file) {
      setSelectedPdfFiles(prev => {
        const updated = { ...prev };
        delete updated[inspectionId];
        return updated;
      });
      return;
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Please select a valid PDF file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('PDF file size must be less than 10MB');
      return;
    }

    setSelectedPdfFiles(prev => ({
      ...prev,
      [inspectionId]: file
    }));
  };

  const handlePdfUpload = async (inspectionId) => {
    const file = selectedPdfFiles[inspectionId];
    if (!file) {
      setError('Please select a PDF file first');
      return;
    }

    try {
      setUploadingPdfForInspection(inspectionId);
      setError(null);

      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      console.log('📤 Uploading PDF for inspection ID:', inspectionId);
      console.log('📤 File:', file.name, file.size, 'bytes');
      console.log('📤 API URL:', `${API_BASE_URL}/inspection/${inspectionId}/`);

      const pdfFormData = new FormData();
      pdfFormData.append('inspection_file', file);

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      };

      // First, try to fetch the inspection to verify it exists
      try {
        const verifyResponse = await axios.get(
          `${API_BASE_URL}/inspection/${inspectionId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('✅ Inspection exists:', verifyResponse.data);
      } catch (verifyError) {
        console.error('❌ Cannot access inspection:', verifyError);
        throw new Error(`Cannot access inspection ID ${inspectionId}. It may have been deleted or you don't have permission to modify it.`);
      }

      const response = await axios.patch(
        `${API_BASE_URL}/inspection/${inspectionId}/`,
        pdfFormData,
        config
      );

      console.log('✅ PDF uploaded successfully:', response.data);

      // Clear the selected file
      setSelectedPdfFiles(prev => {
        const updated = { ...prev };
        delete updated[inspectionId];
        return updated;
      });

      // Refresh inspections to show the uploaded PDF
      await fetchInspections();

    } catch (error) {
      console.error('❌ Error uploading PDF:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });

      let errorMessage = 'Failed to upload PDF';
      if (error.response?.status === 404) {
        errorMessage = `Inspection ID ${inspectionId} not found. It may have been deleted.`;
      } else if (error.response?.status === 403) {
        errorMessage = `You don't have permission to modify this inspection.`;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setError(errorMessage);
    } finally {
      setUploadingPdfForInspection(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
      <div className="relative mx-auto border w-full max-w-6xl shadow-lg rounded-md bg-white flex flex-col" style={{height: 'calc(100vh - 2rem)'}}>
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <h3 className="text-xl font-bold text-gray-900">
            New Inspection - {equipmentName}
            {equipmentSerialNumber && (
              <span className="text-base font-normal text-gray-600 ml-2">
                (S/N: {equipmentSerialNumber})
              </span>
            )}
            {multiInspectionMode && (
              <span className="text-sm font-normal text-blue-600 ml-2">
                ({currentInspectionIndex + 1} of {totalInspections})
              </span>
            )}
            {detectedTableMode && (
              <span className="text-sm font-normal text-green-600 ml-2">
                📋 Table Mode
              </span>
            )}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded mx-5">
            {error}
          </div>
        )}

        {/* Main Content - Two Column Layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Inspection Form */}
          <div className="w-1/2 border-r flex flex-col">
            {detectedTableMode && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded mx-5 mt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <strong>📋 Table Mode Active</strong>
                    <p className="text-sm mt-1">
                      This inspection is opened from a table. After completing this inspection, the table entry will be automatically removed.
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs">Auto-cleanup enabled</div>
                  </div>
                </div>
              </div>
            )}
            {multiInspectionMode && (
              <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded mx-5 mt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <strong>Multi-Equipment Inspection Mode</strong>
                    <p className="text-sm mt-1">
                      You are inspecting multiple equipment items. Complete or skip inspections to proceed to the next item.
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{currentInspectionIndex + 1} / {totalInspections}</div>
                    <div className="text-xs">items remaining</div>
                  </div>
                </div>
              </div>
            )}

            {/* Inspection Form */}
            <div className="p-5 bg-gray-50 flex-1 overflow-y-auto">
              <form id="inspection-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Inspection Type Selection */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Inspection Type <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  {/* Internal inspection option - only visible for inspectors */}
                  {user?.inspector && (
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="inspection_type"
                        value="internal"
                        checked={formData.inspection_type === 'internal'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        <span className="font-medium">Internal</span> - Performing inspection now
                      </span>
                    </label>
                  )}
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="inspection_type"
                      value="external"
                      checked={formData.inspection_type === 'external'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">External</span> - Adding historical/imported inspection
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inspection Date <span className="text-red-500">*</span>
                    {formData.inspection_type === 'internal' && (
                      <span className="text-xs text-gray-500 ml-2">(Current date/time)</span>
                    )}
                  </label>
                  <input
                    type={formData.inspection_type === 'internal' ? 'datetime-local' : 'date'}
                    name="inspection_date"
                    value={formData.inspection_date}
                    onChange={handleInputChange}
                    required
                    disabled={formData.inspection_type === 'internal'}
                    placeholder={formData.inspection_type === 'external' ? 'YYYY-MM-DD' : ''}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      formData.inspection_type === 'internal' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inspector <span className="text-red-500">*</span>
                    {formData.inspection_type === 'internal' && (
                      <span className="text-xs text-gray-500 ml-2">(Auto-filled)</span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="inspector"
                    value={formData.inspector}
                    onChange={handleInputChange}
                    required
                    disabled={formData.inspection_type === 'internal'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      formData.inspection_type === 'internal' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inspection Validity Date <span className="text-red-500">*</span>
                    {formData.status === 'non-ok' && (
                      <span className="text-xs text-gray-500 ml-2">(Auto-set to today for failed inspections)</span>
                    )}
                  </label>
                  <input
                    type="date"
                    name="validity_date"
                    value={formData.validity_date}
                    onChange={handleInputChange}
                    required
                    disabled={formData.status === 'non-ok'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      formData.status === 'non-ok' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="ok">OK</option>
                    <option value="non-ok">Non-OK</option>
                  </select>
                </div>
              </div>

              {/* External Company Name - only for external inspections */}
              {formData.inspection_type === 'external' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    External Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="external_company_name"
                    value={formData.external_company_name}
                    onChange={handleInputChange}
                    required={formData.inspection_type === 'external'}
                    placeholder="Enter the company that performed the inspection"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comments
                </label>
                <textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* PDF Upload - only for external inspections */}
              {formData.inspection_type === 'external' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FileText className="inline h-4 w-4 mr-1" />
                    Inspection PDF Report
                  </label>
                  <input
                    type="file"
                    name="inspection_file"
                    accept="application/pdf"
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {formData.inspection_file && (
                    <p className="mt-1 text-sm text-gray-600">
                      Selected: {formData.inspection_file.name}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Camera className="inline h-4 w-4 mr-1" />
                  Inspection Photo
                </label>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Inspection preview"
                      className="max-w-full h-48 object-cover rounded-md border"
                    />
                  </div>
                )}
              </div>
            </form>
            </div>
          </div>

          {/* Right Panel - Equipment Details / Inspection History */}
          <div className="w-1/2 flex flex-col">
            {/* Header */}
            <div className="p-5 border-b bg-gray-50 flex justify-between items-center">
              <h4 className="text-lg font-semibold">
                {rightPanelTab === 'details'
                  ? 'Details of the equipment being inspected'
                  : 'Inspection History'
                }
              </h4>
              <button
                onClick={() => setRightPanelTab(rightPanelTab === 'details' ? 'history' : 'details')}
                className="text-sm text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
              >
                {rightPanelTab === 'details' ? 'Show inspection history' : 'Show Details'}
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-5 bg-white">
              {rightPanelTab === 'details' ? (
                // Equipment Details View
                <div className="space-y-4">
                  {equipmentData ? (
                    <>
                      {/* Equipment Image - from model or equipment picture */}
                      {(equipmentData.safety_equipment_model_image || equipmentData.safety_equipment_picture) && (
                        <div className="flex justify-center mb-4">
                          <img
                            src={equipmentData.safety_equipment_model_image || equipmentData.safety_equipment_picture}
                            alt={equipmentData.safety_equipment_type_name || equipmentName}
                            className="max-w-full h-64 object-cover rounded-lg border shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      {/* Equipment Details */}
                      <div className="space-y-3">
                        <div className="border-b pb-2">
                          <h5 className="text-sm font-medium text-gray-500">Equipment Type</h5>
                          <p className="text-base font-semibold">{equipmentData.safety_equipment_type_name || equipmentName || 'N/A'}</p>
                        </div>

                        <div className="border-b pb-2">
                          <h5 className="text-sm font-medium text-gray-500">Manufacturer / Brand</h5>
                          <p className="text-base">
                            {equipmentData.safety_equipment_model_manufacturer && equipmentData.safety_equipment_model_brand
                              ? `${equipmentData.safety_equipment_model_manufacturer} / ${equipmentData.safety_equipment_model_brand}`
                              : equipmentData.safety_equipment_model_manufacturer || equipmentData.safety_equipment_model_brand || 'N/A'
                            }
                          </p>
                        </div>

                        <div className="border-b pb-2">
                          <h5 className="text-sm font-medium text-gray-500">Model</h5>
                          <p className="text-base">{equipmentData.safety_equipment_model_name || 'N/A'}</p>
                        </div>

                        <div className="border-b pb-2">
                          <h5 className="text-sm font-medium text-gray-500">Serial Number</h5>
                          <p className="text-base">{equipmentData.safety_equipment_serial_number || 'N/A'}</p>
                        </div>

                        <div className="border-b pb-2">
                          <h5 className="text-sm font-medium text-gray-500">Manufacture Date</h5>
                          <p className="text-base">
                            {equipmentData.safety_equipment_manufacture_date
                              ? new Date(equipmentData.safety_equipment_manufacture_date).toLocaleDateString()
                              : 'N/A'
                            }
                          </p>
                        </div>

                        <div className="border-b pb-2">
                          <h5 className="text-sm font-medium text-gray-500">NFC Tag</h5>
                          <p className="text-base font-mono text-sm">{equipmentData.nfc_tag_number || 'N/A'}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No equipment details available
                    </div>
                  )}
                </div>
              ) : (
                // Inspection History View
                <div className="space-y-3">
              {loading ? (
                <div className="text-center py-4">Loading inspections...</div>
              ) : inspections.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No inspection records found.
                </div>
              ) : (
                inspections.map((inspection, index) => {
                  const status = getInspectionStatus(inspection);
                  const isLatest = index === 0;
                  const isNonOk = inspection.status === 'non-ok';

                  // Calculate inspection title for latest inspection
                  let inspectionTitle = 'Valid';
                  let titleColor = 'text-green-600';
                  if (isLatest) {
                    if (isNonOk) {
                      inspectionTitle = 'Failed';
                      titleColor = 'text-red-600';
                    } else if (status.color === 'red') {
                      inspectionTitle = 'Expired';
                      titleColor = 'text-red-600';
                    }
                  }

                  return (
                    <div key={inspection.id} className={`border rounded-lg p-4 ${isLatest ? 'bg-white border-gray-300' : 'bg-gray-50 border-gray-300'}`}>
                      {/* Latest inspection header with title */}
                      {isLatest && (
                        <div className="mb-3">
                          <h4 className="text-lg">
                            <span className="font-semibold text-gray-700">Latest inspection:</span>
                            <span className={`font-semibold ${titleColor} ml-2`}>
                              {inspectionTitle}
                            </span>
                          </h4>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <span className="text-sm font-medium text-gray-500">Inspection Date:</span>
                            <p className="text-sm">{formatDateTime(inspection.inspection_date)}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Inspector:</span>
                            <p className="text-sm">{inspection.inspector}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Validity:</span>
                            <div className="flex items-center space-x-2">
                              {isNonOk ? (
                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                  None
                                </span>
                              ) : (
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  status.color === 'red' ? 'bg-red-100 text-red-800' :
                                  status.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                                  status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                  status.color === 'green' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {formatDate(inspection.validity_date)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Status:</span>
                            <div className="flex items-center">
                              {inspection.status === 'ok' ? (
                                <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600 mr-1" />
                              )}
                              <span className={`text-sm ${inspection.status === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
                                {inspection.status === 'ok' ? 'OK' : 'Non-OK'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* PDF Report and Inspection Photo */}
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <div className="grid grid-cols-2 gap-4">
                          {/* PDF Report Column */}
                          <div>
                            <span className="text-sm font-medium text-gray-500">PDF Report:</span>
                            <div className="mt-1">
                              {inspection.inspection_file ? (
                                <>
                                  <a
                                    href={inspection.inspection_file}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm underline"
                                  >
                                    <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M4 18h12V6h-4V2H4v16zM9 13V9l-2 2 2 2zm2-4v4l2-2-2-2z"/>
                                    </svg>
                                    Download PDF
                                  </a>
                                  <div className="text-xs text-gray-500 mt-1">
                                    ID: INS-{String(inspection.id).padStart(6, '0')}
                                  </div>
                                </>
                              ) : (
                                <div className="space-y-2">
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) => handlePdfFileSelect(inspection.id, e.target.files[0])}
                                    className="text-sm w-full"
                                  />
                                  <button
                                    onClick={() => handlePdfUpload(inspection.id)}
                                    disabled={!selectedPdfFiles[inspection.id] || uploadingPdfForInspection === inspection.id}
                                    className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {uploadingPdfForInspection === inspection.id ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <Save className="h-3 w-3 mr-1" />
                                        Save
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Inspection Photo Column */}
                          <div>
                            <span className="text-sm font-medium text-gray-500">Inspection Photo:</span>
                            <div className="mt-1">
                              {inspection.inspection_photo ? (
                                <a
                                  href={inspection.inspection_photo}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block cursor-pointer"
                                >
                                  <img
                                    src={inspection.inspection_photo}
                                    alt="Inspection"
                                    className="max-w-full h-20 object-cover rounded-md border hover:opacity-80 transition-opacity"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </a>
                              ) : (
                                <div className="text-xs text-gray-400">
                                  No photo
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="flex justify-end items-center space-x-2 p-5 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
          {multiInspectionMode && (
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Skip This Item
            </button>
          )}
          <button
            type="submit"
            form="inspection-form"
            disabled={loading}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InspectionModal;